import { useEffect, useRef, useState } from 'react';
import { getToken } from '../api.js';

/**
 * A player for audio protected by authentication.
 *
 * `<audio src>` makes the request straight from the browser, where the
 * `Authorization` header cannot be attached — any source under /api would get a
 * 401. We fetch the file and hand it on as a local blob.
 *
 * The download happens on the first press, not at render time: a table with 200
 * rows must not pull 200 files nobody listens to. But a press must also start
 * playback — which is why `play()` is called from an effect that runs AFTER the
 * element has reached the DOM, not straight after `setState`, when the `ref` is
 * still empty.
 */
export default function AudioPlayer({ src, label = 'Listen' }: { src: string; label?: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);

  // Start playback the moment the element actually exists.
  useEffect(() => {
    if (url) void audioRef.current?.play().catch(() => { /* browser policy */ });
  }, [url]);

  async function load() {
    setBusy(true); setErr('');
    try {
      const res = await fetch(src, { headers: { authorization: `Bearer ${getToken() ?? ''}` } });
      if (!res.ok) throw new Error(res.status === 404 ? 'file missing' : `error ${res.status}`);
      setUrl(URL.createObjectURL(await res.blob()));
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'error');
    } finally { setBusy(false); }
  }

  if (err) return <span className="muted" title={err}>⚠ {err}</span>;

  if (!url) {
    return (
      <button onClick={() => void load()} disabled={busy}
              style={{ padding: '3px 10px', fontSize: 12 }}>
        {busy ? '…' : `▶ ${label}`}
      </button>
    );
  }

  return <audio ref={audioRef} controls src={url} />;
}
